/**
 * @module i18n/store
 * @description Internationalization store and helpers
 *
 * Provides reactive locale management with:
 * - Locale switching
 * - Translation lookup with interpolation
 * - Persistent locale preference
 * - Derived translation stores
 *
 * @example
 * import { locale, t, setLocale } from '@/i18n';
 *
 * // Get current locale ('en' or 'fr')
 * const currentLocale = $locale;
 *
 * // Use translations reactively
 * <h1>{$t('common.appName')}</h1>
 *
 * // With interpolation
 * <p>{$t('search.resultsCount', { count: 10 })}</p>
 *
 * // Switch locale
 * setLocale('fr');
 */

import { writable, derived, get } from 'svelte/store';
import { indexedDBService } from '@services/indexedDBService';
import { createLogger } from '@services/loggerService';
import { en } from './en';
import { fr } from './fr';
import {
  type Locale,
  type Translations,
  type InterpolationValues,
  DEFAULT_LOCALE,
  AVAILABLE_LOCALES,
  LOCALE_NAMES,
} from './types';

const log = createLogger('i18n');

// =============================================================================
// TRANSLATIONS MAP
// =============================================================================

/** All available translations */
const translationsMap: Record<Locale, Translations> = {
  en,
  fr,
};

// =============================================================================
// STORE
// =============================================================================

/** Current locale store */
const localeStore = writable<Locale>(DEFAULT_LOCALE);

/** Exported locale store (read-only subscribe) */
export const locale = {
  subscribe: localeStore.subscribe,
};

/** Current translations derived from locale */
export const translations = derived(localeStore, ($locale) => translationsMap[$locale]);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get nested value from object by dot-notation path
 */
function getNestedValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce((current, key) => {
    if (current && typeof current === 'object' && key in current) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Interpolate values into a string
 * Replaces {{key}} with corresponding value
 */
function interpolate(str: string, values?: InterpolationValues): string {
  if (!values) return str;

  return str.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    if (key in values) {
      return String(values[key]);
    }
    return match;
  });
}

/**
 * Get translation by key
 */
function getTranslation(
  translations: Translations,
  key: string,
  values?: InterpolationValues
): string {
  const value = getNestedValue(translations, key);

  if (typeof value === 'string') {
    return interpolate(value, values);
  }

  // Return key if translation not found (helpful for debugging)
  log.warn('Translation not found', { key });
  return key;
}

// =============================================================================
// DERIVED TRANSLATION FUNCTION
// =============================================================================

/**
 * Reactive translation function
 * Use in Svelte components as $t('key') or $t('key', { count: 5 })
 */
export const t = derived(localeStore, ($locale) => {
  const currentTranslations = translationsMap[$locale];
  return (key: string, values?: InterpolationValues): string => {
    return getTranslation(currentTranslations, key, values);
  };
});

// =============================================================================
// ACTIONS
// =============================================================================

/**
 * Initialize locale from storage or browser
 */
export function initLocale(): void {
  log.info('Initializing locale');

  // Try to get stored preference
  const storedLocale = indexedDBService.getLocale();
  if (storedLocale && AVAILABLE_LOCALES.includes(storedLocale)) {
    localeStore.set(storedLocale);
    log.debug('Restored locale from storage', { locale: storedLocale });
    return;
  }

  // Try to detect from browser
  const browserLocale = detectBrowserLocale();
  if (browserLocale) {
    localeStore.set(browserLocale);
    indexedDBService.setLocale(browserLocale);
    log.debug('Detected locale from browser', { locale: browserLocale });
    return;
  }

  // Fall back to default
  log.debug('Using default locale', { locale: DEFAULT_LOCALE });
}

/**
 * Detect locale from browser settings
 */
function detectBrowserLocale(): Locale | null {
  if (typeof navigator === 'undefined') return null;

  const languages = navigator.languages || [navigator.language];

  for (const lang of languages) {
    const code = lang.split('-')[0].toLowerCase();
    if (AVAILABLE_LOCALES.includes(code as Locale)) {
      return code as Locale;
    }
  }

  return null;
}

/**
 * Set the current locale
 */
export function setLocale(newLocale: Locale): void {
  if (!AVAILABLE_LOCALES.includes(newLocale)) {
    log.error('Invalid locale', { locale: newLocale });
    return;
  }

  log.info('Changing locale', { from: get(localeStore), to: newLocale });
  localeStore.set(newLocale);
  indexedDBService.setLocale(newLocale);

  // Update document language attribute
  if (typeof document !== 'undefined') {
    document.documentElement.lang = newLocale;
  }
}

/**
 * Get current locale value
 */
export function getLocale(): Locale {
  return get(localeStore);
}

/**
 * Toggle between available locales (for simple EN/FR switch)
 */
export function toggleLocale(): void {
  const current = get(localeStore);
  const newLocale = current === 'en' ? 'fr' : 'en';
  setLocale(newLocale);
}

/**
 * Get translation synchronously (for non-reactive contexts)
 */
export function translate(key: string, values?: InterpolationValues): string {
  const currentLocale = get(localeStore);
  const currentTranslations = translationsMap[currentLocale];
  return getTranslation(currentTranslations, key, values);
}

// =============================================================================
// UTILITY EXPORTS
// =============================================================================

/**
 * Get available locales with their display names
 */
export function getAvailableLocales(): Array<{ code: Locale; name: string }> {
  return AVAILABLE_LOCALES.map((code) => ({
    code,
    name: LOCALE_NAMES[code],
  }));
}

/**
 * Check if a locale is supported
 */
export function isLocaleSupported(locale: string): locale is Locale {
  return AVAILABLE_LOCALES.includes(locale as Locale);
}

/**
 * Get translations for a specific locale (for SSR or testing)
 */
export function getTranslationsForLocale(locale: Locale): Translations {
  return translationsMap[locale];
}

// =============================================================================
// DERIVED STORES FOR COMMON SECTIONS
// =============================================================================

/** Common translations */
export const commonT = derived(translations, ($t) => $t.common);

/** Auth translations */
export const authT = derived(translations, ($t) => $t.auth);

/** Library translations */
export const libraryT = derived(translations, ($t) => $t.library);

/** Collection translations */
export const collectionT = derived(translations, ($t) => $t.collection);

/** Item translations */
export const itemT = derived(translations, ($t) => $t.item);

/** Search translations */
export const searchT = derived(translations, ($t) => $t.search);

/** Sort translations */
export const sortT = derived(translations, ($t) => $t.sort);

/** Pagination translations */
export const paginationT = derived(translations, ($t) => $t.pagination);

/** Export translations */
export const exportT = derived(translations, ($t) => $t.export);

/** Details panel translations */
export const detailsPanelT = derived(translations, ($t) => $t.detailsPanel);

/** Error translations */
export const errorsT = derived(translations, ($t) => $t.errors);

/** Settings translations */
export const settingsT = derived(translations, ($t) => $t.settings);

/** Accessibility translations */
export const a11yT = derived(translations, ($t) => $t.a11y);

export default t;
